/**
 * AI Trading Lab - Natural Language Query Interface
 *
 * Saleem's test area for NLP queries against all asset types.
 * Uses Gemini 2.5 Pro to interpret queries and return trading data.
 *
 * @version 1.0.0
 */

import { useState, useEffect, useCallback } from 'react';
import {
  FlaskConical,
  Send,
  Loader2,
  RefreshCw,
  Clock,
  Database,
  TrendingUp,
  TrendingDown,
  Minus,
  Sparkles,
  History,
  X,
  ChevronDown,
  AlertCircle,
} from 'lucide-react';
interface TradingLabResponse {
  success: boolean;
  data?: any[];
  count?: number;
  sql?: string;
  summary?: string;
  asset_type?: string;
  query_time_ms?: number;
  model?: string;
}

// Asset types configuration
const ASSET_TYPES = [
  { id: 'all', label: 'All Assets', color: 'bg-purple-500' },
  { id: 'crypto', label: 'Crypto', color: 'bg-orange-500' },
  { id: 'stocks', label: 'Stocks', color: 'bg-blue-500' },
  { id: 'etfs', label: 'ETFs', color: 'bg-green-500' },
  { id: 'forex', label: 'Forex', color: 'bg-cyan-500' },
  { id: 'indices', label: 'Indices', color: 'bg-yellow-500' },
  { id: 'commodities', label: 'Commodities', color: 'bg-amber-500' },
  { id: 'interest_rates', label: 'Interest Rates', color: 'bg-violet-500' },
];

// Preset queries by asset type
const PRESET_QUERIES: Record<string, { label: string; query: string }[]> = {
  all: [
    { label: 'Top Gainers', query: 'Show me the top 10 gainers across all markets today' },
    { label: 'Oversold Assets', query: 'Find oversold assets with RSI below 30 across all types' },
    { label: 'Rise Cycle', query: 'Which assets just started a rise cycle?' },
    { label: 'High Volume', query: 'Show high volume movers across all markets' },
  ],
  crypto: [
    { label: 'Top Crypto Gainers', query: 'Show me top 10 crypto gainers today' },
    { label: 'Oversold Cryptos', query: 'Which cryptos are oversold with RSI below 30?' },
    { label: 'BTC Analysis', query: 'Give me Bitcoin technical analysis with all indicators' },
    { label: 'MACD Crossover', query: 'Find cryptos with bullish MACD crossover' },
    { label: 'Meme Coins', query: 'Show meme coins with high momentum' },
  ],
  stocks: [
    { label: 'Tech Stocks', query: 'Show me top performing tech stocks' },
    { label: 'Oversold Stocks', query: 'Find oversold S&P 500 stocks' },
    { label: 'AAPL vs MSFT', query: 'Compare AAPL and MSFT performance' },
    { label: '52-Week High', query: 'Stocks near 52-week high with good volume' },
    { label: 'High Volume', query: 'Show high volume stocks today' },
  ],
  etfs: [
    { label: 'Sector ETFs', query: 'Best performing sector ETFs this week' },
    { label: 'Bond ETFs', query: 'Bond ETFs with positive momentum' },
    { label: 'SPY vs QQQ', query: 'Compare SPY and QQQ performance' },
    { label: 'Dividend ETFs', query: 'High dividend ETFs with good growth' },
  ],
  forex: [
    { label: 'EUR/USD', query: 'EUR/USD technical analysis' },
    { label: 'Strong Currencies', query: 'Which currencies are strongest today?' },
    { label: 'RSI Divergence', query: 'Forex pairs showing RSI divergence' },
    { label: 'Major Pairs', query: 'Show all major forex pairs with indicators' },
  ],
  indices: [
    { label: 'Global Performance', query: 'Global indices performance comparison' },
    { label: 'S&P vs NASDAQ', query: 'Compare S&P 500 and NASDAQ' },
    { label: 'Asian Markets', query: 'Asian market indices analysis' },
  ],
  commodities: [
    { label: 'Gold & Silver', query: 'Gold and silver price analysis' },
    { label: 'Oil Trends', query: 'Crude oil price trends and indicators' },
    { label: 'Agriculture', query: 'Agricultural commodities with momentum' },
  ],
  interest_rates: [
    { label: 'G20 Rates', query: 'Show all G20 central bank interest rates' },
    { label: 'Rate Changes', query: 'Which countries changed interest rates recently?' },
    { label: 'Highest Rates', query: 'Countries with highest interest rates' },
  ],
};

interface QueryHistoryItem {
  id: string;
  query: string;
  assetType: string;
  timestamp: Date;
  success: boolean;
  recordCount: number;
}

export default function AITradingLab() {
  const [query, setQuery] = useState('');
  const [assetType, setAssetType] = useState('all');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<TradingLabResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<QueryHistoryItem[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [activeLabTool, setActiveLabTool] = useState('query');
  const [fibSymbol, setFibSymbol] = useState('NVDA');
  const [fibLevels, setFibLevels] = useState<any>(null);
  const [correlationData, setCorrelationData] = useState<any>(null);
  const [monteCarloResults, setMonteCarloResults] = useState<any>(null);

  // Load history from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('aiTradingLabHistory');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setHistory(parsed.map((h: QueryHistoryItem) => ({
          ...h,
          timestamp: new Date(h.timestamp)
        })));
      } catch (e) {
        console.error('Failed to parse history:', e);
      }
    }
  }, []);

  // Save history to localStorage
  const saveHistory = useCallback((newHistory: QueryHistoryItem[]) => {
    setHistory(newHistory);
    localStorage.setItem('aiTradingLabHistory', JSON.stringify(newHistory.slice(0, 50)));
  }, []);

  const handleSubmit = async (queryText?: string) => {
    const finalQuery = queryText || query;
    if (!finalQuery.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const API_URL = import.meta.env.VITE_API_URL || 'https://trading-api-1075463475276.us-central1.run.app';
      const res = await fetch(`${API_URL}/api/ai/trading-lab`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: finalQuery, asset_type: assetType })
      });
      const data = await res.json();

      if (data.success || data.data || data.results) {
        const labResult: TradingLabResponse = {
          success: data.success ?? true,
          data: data.data || data.results || [],
          count: data.count || (data.data || data.results || []).length,
          sql: data.sql || data.generated_sql,
          summary: data.summary || data.interpretation,
          asset_type: data.asset_type || assetType,
          query_time_ms: data.query_time_ms || 0,
          model: data.model,
        };
        setResult(labResult);

        // Add to history
        const historyItem: QueryHistoryItem = {
          id: Date.now().toString(),
          query: finalQuery,
          assetType,
          timestamp: new Date(),
          success: true,
          recordCount: labResult.count || 0,
        };
        saveHistory([historyItem, ...history]);
      } else {
        setError(data.error || 'No results returned');
        setResult(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Query failed - check API connection');
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const handlePresetClick = (presetQuery: string) => {
    setQuery(presetQuery);
    handleSubmit(presetQuery);
  };

  const handleHistoryClick = (item: QueryHistoryItem) => {
    setQuery(item.query);
    setAssetType(item.assetType);
    setShowHistory(false);
    handleSubmit(item.query);
  };

  const clearHistory = () => {
    saveHistory([]);
    localStorage.removeItem('aiTradingLabHistory');
  };

  const API_BASE = import.meta.env.VITE_API_URL || 'https://trading-api-1075463475276.us-central1.run.app';

  const fetchFibonacci = async (sym: string) => {
    try {
      const res = await fetch(`${API_BASE}/api/twelvedata/stocks/daily?limit=100`);
      const data = await res.json();
      if (data.success && data.data) {
        const match = data.data.find((d: any) => d.symbol === sym);
        if (match) {
          const high = match.high || match.close * 1.05;
          const low = match.low || match.close * 0.95;
          const diff = high - low;
          setFibLevels({
            symbol: sym, high, low, close: match.close,
            levels: [
              { label: '0% (High)', value: high },
              { label: '23.6%', value: high - diff * 0.236 },
              { label: '38.2%', value: high - diff * 0.382 },
              { label: '50%', value: high - diff * 0.5 },
              { label: '61.8%', value: high - diff * 0.618 },
              { label: '78.6%', value: high - diff * 0.786 },
              { label: '100% (Low)', value: low },
            ]
          });
        }
      }
    } catch { /* ignore */ }
  };

  const fetchCorrelation = async () => {
    try {
      const symbols = ['NVDA', 'AAPL', 'MSFT', 'TSLA', 'AMD', 'META', 'GOOGL', 'AMZN', 'SPY', 'QQQ'];
      const res = await fetch(`${API_BASE}/api/twelvedata/stocks/daily?limit=100`);
      const data = await res.json();
      if (data.success && data.data) {
        const priceMap: Record<string, number> = {};
        data.data.forEach((d: any) => { priceMap[d.symbol] = d.growth_score || d.rsi || 50; });
        const matched = symbols.filter(s => priceMap[s] !== undefined);
        setCorrelationData({ symbols: matched, scores: matched.map(s => priceMap[s]) });
      }
    } catch { /* ignore */ }
  };

  const runMonteCarlo = async (sym: string) => {
    try {
      const res = await fetch(`${API_BASE}/api/twelvedata/stocks/daily?limit=100`);
      const data = await res.json();
      if (data.success && data.data) {
        const match = data.data.find((d: any) => d.symbol === sym);
        if (match) {
          const price = match.close || 100;
          const dailyReturn = 0.0005; // ~12% annual
          const dailyVol = 0.02; // ~32% annual vol
          const paths: number[][] = [];
          for (let p = 0; p < 100; p++) {
            const path = [price];
            for (let d = 1; d <= 60; d++) {
              const rand = (Math.random() - 0.5) * 2;
              path.push(path[d - 1] * (1 + dailyReturn + dailyVol * rand));
            }
            paths.push(path);
          }
          // Calculate percentiles at each step
          const percentiles = Array.from({ length: 61 }, (_, d) => {
            const vals = paths.map(p => p[d]).sort((a, b) => a - b);
            return {
              p10: vals[Math.floor(vals.length * 0.1)],
              p25: vals[Math.floor(vals.length * 0.25)],
              p50: vals[Math.floor(vals.length * 0.5)],
              p75: vals[Math.floor(vals.length * 0.75)],
              p90: vals[Math.floor(vals.length * 0.9)],
            };
          });
          setMonteCarloResults({ symbol: sym, startPrice: price, days: 60, percentiles });
        }
      }
    } catch { /* ignore */ }
  };

  const formatValue = (value: unknown): string => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'number') {
      if (Math.abs(value) >= 1000000) return `${(value / 1000000).toFixed(2)}M`;
      if (Math.abs(value) >= 1000) return `${(value / 1000).toFixed(2)}K`;
      if (Number.isInteger(value)) return value.toString();
      return value.toFixed(4);
    }
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
  };

  const getSignalColor = (value: unknown): string => {
    if (typeof value === 'string') {
      const v = value.toUpperCase();
      if (v.includes('STRONG_BUY') || v.includes('BULLISH')) return 'text-green-400';
      if (v.includes('BUY') || v.includes('UP')) return 'text-green-300';
      if (v.includes('STRONG_SELL') || v.includes('BEARISH')) return 'text-red-400';
      if (v.includes('SELL') || v.includes('DOWN')) return 'text-red-300';
    }
    if (typeof value === 'number') {
      if (value > 70) return 'text-green-400';
      if (value > 50) return 'text-green-300';
      if (value < 30) return 'text-red-400';
      if (value < 50) return 'text-red-300';
    }
    return 'text-slate-300';
  };

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-500/20 rounded-xl">
              <FlaskConical className="w-8 h-8 text-purple-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                AI Trading Lab
                <span className="px-2 py-0.5 text-xs bg-purple-500/30 text-purple-300 rounded-full">
                  TEST
                </span>
              </h1>
              <p className="text-slate-400 text-sm">
                Ask anything about market data using natural language
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-slate-300 transition-colors"
            >
              <History className="w-4 h-4" />
              History ({history.length})
            </button>
          </div>
        </div>
      </div>

      {/* Lab Tool Selector */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {[
          { id: 'query', label: 'NLP Query' },
          { id: 'correlation', label: 'Correlation' },
          { id: 'fibonacci', label: 'Fibonacci' },
          { id: 'montecarlo', label: 'Monte Carlo' },
          { id: 'elliott', label: 'Elliott Wave' },
        ].map(t => (
          <button key={t.id} onClick={() => setActiveLabTool(t.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeLabTool === t.id ? 'bg-purple-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {activeLabTool === 'query' && (<>
      {/* Asset Type Tabs */}
      <div className="mb-4 flex flex-wrap gap-2">
        {ASSET_TYPES.map((type) => (
          <button
            key={type.id}
            onClick={() => setAssetType(type.id)}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              assetType === type.id
                ? `${type.color} text-white shadow-lg`
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white'
            }`}
          >
            {type.label}
          </button>
        ))}
      </div>

      {/* Query Input */}
      <div className="mb-4 bg-slate-800 rounded-xl p-4 border border-slate-700">
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <textarea
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              placeholder="Ask anything about market data... e.g., 'Show me oversold cryptos with RSI below 30'"
              className="w-full h-24 px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
            />
            <div className="absolute bottom-3 right-3 text-xs text-slate-500">
              Press Enter to submit
            </div>
          </div>
          <button
            onClick={() => handleSubmit()}
            disabled={loading || !query.trim()}
            className="px-6 py-3 bg-purple-600 hover:bg-purple-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-white rounded-lg font-medium flex items-center gap-2 transition-colors h-fit"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
            Ask AI
          </button>
        </div>
      </div>

      {/* Preset Queries */}
      <div className="mb-6">
        <div className="text-sm text-slate-400 mb-2 flex items-center gap-2">
          <Sparkles className="w-4 h-4" />
          Quick Queries:
        </div>
        <div className="flex flex-wrap gap-2">
          {PRESET_QUERIES[assetType]?.map((preset, idx) => (
            <button
              key={idx}
              onClick={() => handlePresetClick(preset.query)}
              disabled={loading}
              className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-slate-300 rounded-lg text-sm transition-colors border border-slate-700"
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-400" />
          <span className="text-red-300">{error}</span>
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
          {/* Results Header */}
          <div className="p-4 border-b border-slate-700 bg-slate-800/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Database className="w-4 h-4 text-purple-400" />
                  <span className="text-white font-medium">
                    {result.count} Results
                  </span>
                </div>
                <div className="text-slate-400 text-sm">
                  Asset: <span className="text-slate-300 uppercase">{result.asset_type}</span>
                </div>
                <div className="text-slate-400 text-sm flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {result.query_time_ms}ms
                </div>
                {result.model && (
                  <div className="text-slate-400 text-sm">
                    Model: <span className="text-purple-300">{result.model}</span>
                  </div>
                )}
              </div>
              {result.success && (
                <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs">
                  Success
                </span>
              )}
            </div>
          </div>

          {/* Summary */}
          {result.summary && (
            <div className="p-4 bg-purple-500/10 border-b border-slate-700">
              <div className="flex items-start gap-2">
                <Sparkles className="w-5 h-5 text-purple-400 mt-0.5" />
                <div>
                  <div className="text-purple-300 font-medium mb-1">AI Summary</div>
                  <div className="text-slate-300">{result.summary}</div>
                </div>
              </div>
            </div>
          )}

          {/* Data Table */}
          {result.data && result.data.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-900/50">
                  <tr>
                    {Object.keys(result.data[0]).slice(0, 10).map((key) => (
                      <th
                        key={key}
                        className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider"
                      >
                        {key.replace(/_/g, ' ')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {result.data.slice(0, 50).map((row, idx) => (
                    <tr key={idx} className="hover:bg-slate-700/50">
                      {Object.entries(row).slice(0, 10).map(([key, value], cidx) => (
                        <td
                          key={cidx}
                          className={`px-4 py-3 text-sm whitespace-nowrap ${getSignalColor(value)}`}
                        >
                          {formatValue(value)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {result.data.length > 50 && (
                <div className="p-4 text-center text-slate-400 text-sm border-t border-slate-700">
                  Showing 50 of {result.data.length} results
                </div>
              )}
            </div>
          )}

          {/* SQL Query (collapsible) */}
          {result.sql && (
            <details className="border-t border-slate-700">
              <summary className="p-4 cursor-pointer text-slate-400 hover:text-white flex items-center gap-2">
                <ChevronDown className="w-4 h-4" />
                View Generated SQL
              </summary>
              <pre className="p-4 bg-slate-900 text-xs text-slate-300 overflow-x-auto">
                {result.sql}
              </pre>
            </details>
          )}
        </div>
      )}
      </>)}

      {activeLabTool === 'fibonacci' && (
        <div className="bg-gray-900 rounded-xl p-6 border border-gray-700">
          <h3 className="text-white text-lg font-bold mb-4">Fibonacci Retracement Calculator</h3>
          <div className="flex gap-2 mb-4">
            <input value={fibSymbol} onChange={e => setFibSymbol(e.target.value.toUpperCase())}
              className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm" placeholder="Symbol..." />
            <button onClick={() => fetchFibonacci(fibSymbol)}
              className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-medium">Calculate</button>
          </div>
          {fibLevels && (
            <div>
              <div className="text-gray-400 text-sm mb-2">
                {fibLevels.symbol}: Current ${Number(fibLevels.close).toFixed(2)} | High ${Number(fibLevels.high).toFixed(2)} | Low ${Number(fibLevels.low).toFixed(2)}
              </div>
              <div className="space-y-2">
                {fibLevels.levels.map((l: any) => {
                  const pctFromPrice = ((l.value - fibLevels.close) / fibLevels.close * 100);
                  const isAbove = l.value > fibLevels.close;
                  return (
                    <div key={l.label} className="flex items-center gap-3 bg-gray-800 rounded-lg px-4 py-2">
                      <span className="text-gray-400 text-sm w-24">{l.label}</span>
                      <span className="text-white font-bold">${Number(l.value).toFixed(2)}</span>
                      <span className={`text-xs ${isAbove ? 'text-green-400' : 'text-red-400'}`}>
                        {pctFromPrice > 0 ? '+' : ''}{pctFromPrice.toFixed(1)}%
                      </span>
                      <div className="flex-1 h-2 bg-gray-700 rounded">
                        <div className={`h-full rounded ${l.label.includes('50%') ? 'bg-yellow-500' : l.label.includes('61.8') ? 'bg-purple-500' : 'bg-blue-500'}`}
                          style={{ width: `${Math.max(5, 100 - Math.abs(pctFromPrice) * 5)}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {activeLabTool === 'correlation' && (
        <div className="bg-gray-900 rounded-xl p-6 border border-gray-700">
          <h3 className="text-white text-lg font-bold mb-4">Growth Score Correlation Matrix</h3>
          <button onClick={fetchCorrelation} className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-medium mb-4">Load Data</button>
          {correlationData && (
            <div className="grid grid-cols-5 gap-2">
              {correlationData.symbols.map((s: string, i: number) => (
                <div key={s} className="bg-gray-800 rounded-lg p-3 text-center">
                  <div className="text-white text-sm font-bold">{s}</div>
                  <div className={`text-lg font-bold ${correlationData.scores[i] >= 60 ? 'text-green-400' : correlationData.scores[i] >= 40 ? 'text-yellow-400' : 'text-red-400'}`}>
                    {Number(correlationData.scores[i]).toFixed(0)}
                  </div>
                  <div className="text-gray-500 text-xs">Growth Score</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeLabTool === 'montecarlo' && (
        <div className="bg-gray-900 rounded-xl p-6 border border-gray-700">
          <h3 className="text-white text-lg font-bold mb-4">Monte Carlo Simulation (60 Days)</h3>
          <div className="flex gap-2 mb-4">
            <input value={fibSymbol} onChange={e => setFibSymbol(e.target.value.toUpperCase())}
              className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm" placeholder="Symbol..." />
            <button onClick={() => runMonteCarlo(fibSymbol)}
              className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-medium">Run Simulation</button>
          </div>
          {monteCarloResults && (
            <div>
              <div className="text-gray-400 text-sm mb-3">
                {monteCarloResults.symbol}: Starting at ${Number(monteCarloResults.startPrice).toFixed(2)}, 100 paths, {monteCarloResults.days} days
              </div>
              <svg width="100%" height="200" viewBox="0 0 600 200" className="bg-gray-800 rounded-lg">
                {/* P10-P90 fan */}
                <polygon
                  points={monteCarloResults.percentiles.map((p: any, i: number) => `${i * 10},${200 - ((p.p90 / monteCarloResults.startPrice - 0.85) / 0.3) * 200}`).join(' ') + ' ' +
                    [...monteCarloResults.percentiles].reverse().map((p: any, i: number) => `${(60 - i) * 10},${200 - ((p.p10 / monteCarloResults.startPrice - 0.85) / 0.3) * 200}`).join(' ')}
                  fill="#a855f720" />
                {/* P25-P75 fan */}
                <polygon
                  points={monteCarloResults.percentiles.map((p: any, i: number) => `${i * 10},${200 - ((p.p75 / monteCarloResults.startPrice - 0.85) / 0.3) * 200}`).join(' ') + ' ' +
                    [...monteCarloResults.percentiles].reverse().map((p: any, i: number) => `${(60 - i) * 10},${200 - ((p.p25 / monteCarloResults.startPrice - 0.85) / 0.3) * 200}`).join(' ')}
                  fill="#a855f740" />
                {/* Median line */}
                <polyline
                  points={monteCarloResults.percentiles.map((p: any, i: number) => `${i * 10},${200 - ((p.p50 / monteCarloResults.startPrice - 0.85) / 0.3) * 200}`).join(' ')}
                  fill="none" stroke="#a855f7" strokeWidth="2" />
              </svg>
              <div className="flex justify-between mt-2 text-xs text-gray-500">
                <span>Day 0</span>
                <span>P10: ${monteCarloResults.percentiles[60]?.p10.toFixed(2)}</span>
                <span>P50: ${monteCarloResults.percentiles[60]?.p50.toFixed(2)}</span>
                <span>P90: ${monteCarloResults.percentiles[60]?.p90.toFixed(2)}</span>
                <span>Day 60</span>
              </div>
            </div>
          )}
        </div>
      )}

      {activeLabTool === 'elliott' && (
        <div className="bg-gray-900 rounded-xl p-6 border border-gray-700 text-center">
          <h3 className="text-white text-lg font-bold mb-4">Elliott Wave Analysis</h3>
          <div className="text-gray-400 mb-4">
            <svg width="300" height="150" viewBox="0 0 300 150" className="mx-auto mb-4">
              <polyline points="10,130 50,60 80,100 130,20 160,80 200,40 250,110 290,70" fill="none" stroke="#a855f7" strokeWidth="2" />
              <text x="45" y="55" fill="#10b981" fontSize="12">1</text>
              <text x="75" y="105" fill="#ef4444" fontSize="12">2</text>
              <text x="125" y="15" fill="#10b981" fontSize="12">3</text>
              <text x="155" y="85" fill="#ef4444" fontSize="12">4</text>
              <text x="195" y="35" fill="#10b981" fontSize="12">5</text>
              <text x="245" y="115" fill="#3b82f6" fontSize="12">A</text>
              <text x="285" y="65" fill="#3b82f6" fontSize="12">B</text>
            </svg>
          </div>
          <p className="text-gray-400 text-sm mb-2">Elliott Wave theory identifies 5-wave impulse patterns and 3-wave corrective patterns.</p>
          <p className="text-gray-500 text-xs">Automatic wave detection coming soon. Currently showing educational reference.</p>
          <div className="mt-4 grid grid-cols-2 gap-3 text-left max-w-md mx-auto">
            <div className="bg-gray-800 rounded-lg p-3">
              <div className="text-green-400 text-sm font-bold">Impulse (1-2-3-4-5)</div>
              <div className="text-gray-400 text-xs">Moves in direction of main trend. Wave 3 is usually the longest.</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-3">
              <div className="text-blue-400 text-sm font-bold">Corrective (A-B-C)</div>
              <div className="text-gray-400 text-xs">Counter-trend correction. Usually retraces 38.2-61.8% of impulse.</div>
            </div>
          </div>
        </div>
      )}

      {/* History Sidebar */}
      {showHistory && (
        <div className="fixed inset-y-0 right-0 w-80 bg-slate-800 border-l border-slate-700 shadow-xl z-50 overflow-hidden flex flex-col">
          <div className="p-4 border-b border-slate-700 flex items-center justify-between">
            <h3 className="font-medium text-white flex items-center gap-2">
              <History className="w-4 h-4" />
              Query History
            </h3>
            <div className="flex items-center gap-2">
              {history.length > 0 && (
                <button
                  onClick={clearHistory}
                  className="text-xs text-red-400 hover:text-red-300"
                >
                  Clear All
                </button>
              )}
              <button
                onClick={() => setShowHistory(false)}
                className="p-1 hover:bg-slate-700 rounded"
              >
                <X className="w-4 h-4 text-slate-400" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {history.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                No queries yet
              </div>
            ) : (
              <div className="divide-y divide-slate-700">
                {history.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleHistoryClick(item)}
                    className="w-full p-4 text-left hover:bg-slate-700/50 transition-colors"
                  >
                    <div className="text-sm text-white line-clamp-2 mb-1">
                      {item.query}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <span className="uppercase">{item.assetType}</span>
                      <span>•</span>
                      <span>{item.recordCount} results</span>
                      <span>•</span>
                      <span>
                        {new Date(item.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
